import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import Header from './components/shared/Header';
import Footer from './components/shared/Footer';
import ScrollToTop from './components/shared/ScrollToTop';

// Lazy load modals/sidebars since they're not needed for initial render
const LocationSelectionModalWrapper = lazy(() => import('./components/shared/LocationSelectionModalWrapper'));
const WaitlistSidebarWrapper = lazy(() => import('./components/shared/WaitlistSidebarWrapper'));
const OrderConfirmationModal = lazy(() => import('./components/shared/OrderConfirmationModal'));
import HomePage from './pages/home';
import StylesPage from './pages/styles';
import LocationsPage from './pages/locations';
import LocationPage from './pages/locations/[slug]';
import MenuPage from './pages/menu';
import MenuCategoryPage from './pages/menu/[category]';
import MenuItemWrapper from './pages/menu/MenuItemWrapper';
import LoyaltyPage from './pages/loyalty';
import GiftCardsPage from './pages/gift-cards';
import AboutPage from './pages/about';
import ContactPage from './pages/contact';
import PrivacyPolicyPage from './pages/privacy-policy';
import StoreRequestsPage from './pages/store-requests';
// No longer using individual menu item pages
import MenuTestPage from './pages/menu/test';
import { RegionProvider } from './context/RegionContext';
import { LocationProvider } from './context/LocationContext';
import { NavProvider } from './context/NavContext';
import { HeaderProvider } from './context/HeaderContext';
import { LocationModalProvider, useLocationModal } from './context/LocationModalContext';
import { WaitlistProvider, useWaitlist } from './context/WaitlistContext';
import { OrderConfirmationProvider, useOrderConfirmation } from './context/OrderConfirmationContext';
import MenuDrawerProvider from './components/shared/MenuDrawerProvider';

// Separate component to access context hooks
function AppContent() {
  const { isModalOpen } = useLocationModal();
  const { isWaitlistOpen } = useWaitlist();
  const { isOpen: isOrderConfirmationOpen, locationName, closeConfirmation, proceedToOrder } = useOrderConfirmation();

  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen flex flex-col bg-brand-cream">
        <Header />
        <main className="flex-grow">
          <Routes>
            <Route path="/styleguide" element={<StylesPage />} />
            <Route path="/locations" element={<LocationsPage />} />
            <Route path="/locations/:slug" element={<LocationPage />} />
            <Route path="/loyalty" element={<LoyaltyPage />} />
            <Route path="/gift-cards" element={<GiftCardsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/store-requests" element={<StoreRequestsPage />} />
            <Route path="/menu/test" element={<MenuTestPage />} />
            {/* Menu routes wrapped with MenuDrawerProvider */}
            <Route path="/menu" element={<MenuDrawerProvider><MenuPage /></MenuDrawerProvider>} />
            <Route path="/menu/:category" element={<MenuDrawerProvider><MenuCategoryPage /></MenuDrawerProvider>} />
            <Route path="/menu/:category/:item" element={<MenuDrawerProvider><MenuItemWrapper /></MenuDrawerProvider>} />
            <Route path="/" element={<HomePage />} />
          </Routes>
        </main>
        <Footer />
        
        {/* Only render modals when they're actually opened */}
        {isModalOpen && (
          <Suspense fallback={null}>
            <LocationSelectionModalWrapper />
          </Suspense>
        )}
        {isWaitlistOpen && (
          <Suspense fallback={null}>
            <WaitlistSidebarWrapper />
          </Suspense>
        )}
        {isOrderConfirmationOpen && (
          <Suspense fallback={null}>
            <OrderConfirmationModal
              isOpen={isOrderConfirmationOpen}
              onClose={closeConfirmation}
              onConfirm={proceedToOrder}
              locationName={locationName}
            />
          </Suspense>
        )}
      </div>
    </Router>
  );
}

function App() {
  return (
    <LocationProvider>
      <RegionProvider>
        <NavProvider>
          <HeaderProvider>
            <LocationModalProvider>
              <WaitlistProvider>
                <OrderConfirmationProvider>
                  <AppContent />
                </OrderConfirmationProvider>
              </WaitlistProvider>
            </LocationModalProvider>
          </HeaderProvider>
        </NavProvider>
      </RegionProvider>
    </LocationProvider>
  );
}

export default App;